import ShareModal from "@/components/ShareModal";
import { cn } from "@/lib/utils";
import { useProjectModalStore } from "@/store/zustand/projectModalStore";
import { useProjectCacheStore } from "@/store/zustand/projectHistoryStore";
import { useProjectId } from "@/utils/utils";
import {
  ChevronDown,
  Ellipsis,
  Link as LucideLink,
  PlusIcon,
  Search,
  TrashIcon,
  X,
  Check,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router";
import HireHumanModal from "../AssetsPanel/HireHumanModal";
import Icon, { ICONS } from "../Core/Icons";
import { Button } from "../ui/button";
import UserMenu from "./UserMenu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";
import { Pencil1Icon } from "@radix-ui/react-icons";
import {
  setDeleteProjectCandidateId,
  setEditProjectCandidateId,
  setShowAddEditProjectModal,
  setShowDeleteProjectModal,
} from "@/store/projectsSlice";

interface Project {
  id: string;
  name: string;
  repoPath?: string;
}

interface Props {
  label?: string;
  hideCreateProject?: boolean;
  showProjectDropdown?: boolean;
}

// Subtle, muted color palette for project badges
const BADGE_COLORS = [
  "bg-blue-600/40",
  "bg-purple-600/40",
  "bg-pink-600/40",
  "bg-amber-600/40",
  "bg-emerald-600/40",
  "bg-cyan-600/40",
  "bg-indigo-600/40",
  "bg-rose-600/40",
  "bg-teal-600/40",
  "bg-violet-600/40",
];

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) {
    return (
      (words[0][0] || "").toUpperCase() + (words[1][0] || "").toUpperCase()
    );
  }
  return name.substring(0, 2).toUpperCase();
};

const getBadgeColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return BADGE_COLORS[Math.abs(hash) % BADGE_COLORS.length];
};

// Sub-menu for project actions (Edit, Delete, Open in New Tab)
const ProjectItemMenu = React.memo(
  ({
    project,
    onSelect,
    isSelected,
  }: {
    project: Project;
    onSelect: () => void;
    isSelected: boolean;
  }) => {
    const dispatch = useDispatch();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const handleEditClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      dispatch(setEditProjectCandidateId(project.id));
      dispatch(setShowAddEditProjectModal(true));
      setIsMenuOpen(false);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      dispatch(setDeleteProjectCandidateId(project.id));
      dispatch(setShowDeleteProjectModal(true));
      setIsMenuOpen(false);
    };

    const handleOpenInNewTab = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const baseURL = window.location.origin;
      window.open(`${baseURL}/?project_id=${project.id}`, "_blank");
      setIsMenuOpen(false);
    };

    return (
      <div
        className={cn(
          "group flex items-center w-full gap-2 px-2 py-1.5 rounded-sm cursor-pointer hover:bg-secondary-n-800",
          isSelected && "bg-secondary-n-900",
        )}
        onClick={onSelect}
      >
        {isSelected ? (
          <div className="size-5 rounded-full bg-secondary-n-700 flex items-center justify-center shrink-0">
            <Check className="size-2.5 text-white" />
          </div>
        ) : (
          <div
            className={cn(
              "size-5 rounded-full flex items-center justify-center text-[9px] font-medium text-white shrink-0",
              getBadgeColor(project.name),
            )}
          >
            {getInitials(project.name)}
          </div>
        )}
        <div className="flex-1 min-w-0 text-xs truncate">{project.name}</div>
        <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(true);
              }}
              className={cn(
                "size-5 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 shrink-0 hover:bg-secondary-n-700",
                isMenuOpen && "opacity-100 bg-secondary-n-700",
              )}
            >
              <Ellipsis className="size-3.5 text-secondary-n-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-[160px]">
            <DropdownMenuItem
              onClick={handleOpenInNewTab}
              className="text-xs cursor-pointer"
            >
              <LucideLink className="mr-2 h-3 w-3" />
              Open in New Tab
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleEditClick}
              className="text-xs cursor-pointer"
            >
              <Pencil1Icon className="mr-2 h-3 w-3" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuSeparator className="-mx-1" />
            <DropdownMenuItem
              onClick={handleDeleteClick}
              className="text-xs text-destructive hover:text-destructive cursor-pointer"
            >
              <TrashIcon className="mr-2 h-3 w-3" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  },
);

const AppHeader: React.FC<Props> = React.memo((props) => {
  const { showProjectDropdown = false } = props;
  const navigate = useNavigate();
  const projects = useSelector(
    (state: any) => state.supabaseStore.projects,
  ) as Record<string, Project>;
  const projectId = useProjectId();
  const project = projectId ? projects[projectId] : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showHireHumanModal, setShowHireHumanModal] = useState(false);

  // Get recent project IDs from cache store - memoize the getter
  const getRecentProjectIds = useCallback(() => {
    return useProjectCacheStore.getState().getRecentlyAccessedProjectIds();
  }, []);

  // Sort projects alphabetically and filter based on search
  const sortedProjects = useMemo(() => {
    return Object.values(projects || {})
      .filter(
        (p: Project) =>
          p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (p.repoPath || "").toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((a: Project, b: Project) => a.name.localeCompare(b.name));
  }, [projects, searchQuery]);

  // Get recent projects (only when not searching)
  const recentProjects = useMemo(() => {
    if (searchQuery) return [];
    const recentIds = getRecentProjectIds();
    return recentIds
      .map((id) => projects[id])
      .filter((p): p is Project => p !== undefined);
  }, [projects, searchQuery, getRecentProjectIds]);

  // Get remaining projects (exclude recent ones when not searching)
  const otherProjects = useMemo(() => {
    if (searchQuery) return sortedProjects;
    const recentIds = new Set(getRecentProjectIds());
    return sortedProjects.filter((p) => !recentIds.has(p.id));
  }, [sortedProjects, searchQuery, getRecentProjectIds]);

  // Auto-select first project if no project is selected (only on home page)
  useEffect(() => {
    if (
      showProjectDropdown &&
      !projectId &&
      sortedProjects.length > 0 &&
      window.location.pathname === "/"
    ) {
      try {
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("project_id", sortedProjects[0].id);
          return newParams;
        });
        // Record access for recent projects
        useProjectCacheStore
          .getState()
          .recordProjectAccess(sortedProjects[0].id);
      } catch (error) {
        console.warn("Failed to auto-select project:", error);
      }
    }
  }, [projectId, sortedProjects, setSearchParams, showProjectDropdown]);

  // Register project dropdown with modal store
  useEffect(() => {
    const modalStore = useProjectModalStore.getState();

    if (isDropdownOpen) {
      modalStore.addProjectModal("project-dropdown");
    } else {
      modalStore.removeProjectModal("project-dropdown");
    }
  }, [isDropdownOpen]);

  const handleProjectSelect = (selectedProject: Project) => {
    setSearchParams((prev) => {
      prev.set("project_id", selectedProject.id);
      return prev;
    });
    // Record access for recent projects
    useProjectCacheStore.getState().recordProjectAccess(selectedProject.id);
    setIsDropdownOpen(false);
    setSearchQuery(""); // Reset search when closing
  };

  return (
    <nav className="border-b px-2 shrink-0 flex items-center justify-between z-2002 relative h-11">
      <div className="flex-row flex grow justify-between items-center ml-0 my-0">
        <div className="flex flex-1 grow items-center">
          <Link to="/">
            <Icon
              icon={ICONS.TEMPO_LOGO_WHITE}
              className="flex grow-0 w-5 h-5 cursor-pointer mr-2 p-1.5 hover:bg-secondary-100 hover:scale-105 hover:shadow-xs rounded-sm transition-all duration-200 ease-out"
            />
          </Link>
          {showProjectDropdown && (
            <DropdownMenu
              open={isDropdownOpen}
              onOpenChange={(open) => {
                setIsDropdownOpen(open);
                if (!open) setSearchQuery("");
              }}
            >
              <DropdownMenuTrigger
                asChild
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
              >
                <button className="flex items-center gap-1.5 px-2 h-7 rounded hover:bg-secondary-n-900 text-xs text-white font-semibold max-w-[200px]">
                  <span className="truncate">
                    {project?.name || "Select Project"}
                  </span>
                  <ChevronDown className="size-3.5 shrink-0" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[280px] p-2"
                onPointerDownOutside={() => setIsDropdownOpen(false)}
              >
                {/* Search input */}
                <div
                  className="relative mb-2"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 size-4 text-secondary-n-500" />
                  <Input
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "ArrowDown") {
                        e.preventDefault();
                        // Focus first menu item
                        const firstItem = document.querySelector(
                          '[role="menuitem"]',
                        ) as HTMLElement;
                        firstItem?.focus();
                      } else if (e.key === "Enter") {
                        e.preventDefault();
                        // Select first project if available
                        const firstProject =
                          recentProjects[0] || otherProjects[0];
                        if (firstProject) {
                          handleProjectSelect(firstProject);
                        }
                      }
                    }}
                    className="pl-8 pr-8 h-8 text-xs bg-secondary-n-950 border-secondary-n-800"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary-n-500 hover:text-secondary-n-300"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>

                {/* Project list */}
                <div className="max-h-[300px] overflow-y-auto">
                  {sortedProjects.length === 0 ? (
                    <div className="text-xs text-secondary-n-500 py-2 px-2">
                      No projects found
                    </div>
                  ) : (
                    <>
                      {/* Recent projects section */}
                      {recentProjects.length > 0 && (
                        <>
                          <DropdownMenuLabel className="text-[10px] text-secondary-n-500 font-normal px-2 py-1">
                            Recent
                          </DropdownMenuLabel>
                          {recentProjects.map((p) => (
                            <ProjectItemMenu
                              key={p.id}
                              project={p}
                              onSelect={() => handleProjectSelect(p)}
                              isSelected={projectId === p.id}
                            />
                          ))}
                        </>
                      )}

                      {/* All projects section */}
                      {otherProjects.length > 0 && (
                        <>
                          {recentProjects.length > 0 && (
                            <DropdownMenuLabel className="text-[10px] text-secondary-n-500 font-normal px-2 py-1 mt-2">
                              All Projects
                            </DropdownMenuLabel>
                          )}
                          {otherProjects.map((p) => (
                            <ProjectItemMenu
                              key={p.id}
                              project={p}
                              onSelect={() => handleProjectSelect(p)}
                              isSelected={projectId === p.id}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}
                </div>

                <DropdownMenuSeparator className="my-2 -mx-2" />

                {/* New Project button */}
                <DropdownMenuItem
                  onClick={() => {
                    navigate("/project-create/workflow-select");
                    setIsDropdownOpen(false);
                  }}
                  className="text-xs cursor-pointer py-1.5 hover:bg-secondary-n-800 focus:bg-secondary-n-800 data-highlighted:bg-secondary-n-800"
                >
                  <PlusIcon className="size-3.5 mr-2" />
                  New Project
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div
            className={cn(
              "text-xs font-normal text-secondary-800",
              !props.label && "hidden",
            )}
          >
            {props.label}
          </div>
          <div className="flex flex-1 grow"></div>
        </div>
        {showProjectDropdown && (
          <>
            <Button
              variant="primary"
              size="sm"
              className="h-[28px] rounded-md bg-background-CTA mx-2 px-2 flex items-center"
              onClick={() => {
                navigate("/project-create/workflow-select");
              }}
            >
              <PlusIcon className="size-3.5" />
              <div className="text-xs font-normal ml-1">Create Project</div>
            </Button>
            <div className="flex items-center">
              <UserMenu />
            </div>
          </>
        )}
      </div>
      {showShareModal && (
        <ShareModal closeModal={() => setShowShareModal(false)} />
      )}
      {showHireHumanModal && (
        <HireHumanModal closeModal={() => setShowHireHumanModal(false)} />
      )}
    </nav>
  );
});

export default AppHeader;
